import moment from 'moment'; // Import Moment.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useCurrentUser from 'src/hooks/use-current-user';
import {
  getTimeCheckInAction,
  getTimeCheckInActionReset,
  postCheckInOutActionReset,
  postTimeCheckOutAction,
  postTimeCheckOutActionResetLogout,
} from 'src/redux/actions/therapist-dashboard-action';
import { logoutUser } from 'src/routes/AuthContext';
import {
  convertDateToFacilityDate,
  convertIntoIsoFormat,
} from 'src/utils/convertDateToSelectedTimeZone';

interface StopwatchContextProps {
  formattedTime: string;
  isRunning: boolean;
  startStopwatch: any;
  stopStopwatch: () => void;
  // resetStopwatch: any;
  time: any;
  setIsRunning: any;
  globalLocationType: any;
}

function calculateTimeDifference(startTimer: any, timer: any) {
  const start = moment(startTimer);
  const end = moment(timer);

  const duration = moment.duration(end.diff(start));

  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  const timeDiffString = `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  return timeDiffString;
}

const StopwatchContext = createContext<StopwatchContextProps | undefined>(undefined);

export const StopwatchProvider: React.FC<any> = ({ children }: any) => {
  const dispatch = useDispatch();

  const [providerId] = useCurrentUser(['providerId']);

  //get
  const getTimeCheckIn = useSelector((state: any) => state.checkInReducer);

  // post
  const checkoutResponse = useSelector((state: any) => state.checkoutReducer);

  const [time, setTime] = useState<number>(
    parseInt(localStorage.getItem('lastRunningTime') || '0')
  );
  const [isRunning, setIsRunning] = useState<boolean>(
    localStorage.getItem('isCheckIn') === 'true' ? true : false
  );

  const [globalLocationType, setGlobalLocationType] = useState(null);

  useEffect(() => {
    if (!getTimeCheckIn?.isGetCheckIn && getTimeCheckIn?.response === null) return;
    if (!getTimeCheckIn.isGetCheckIn && getTimeCheckIn?.response !== null) {
      return;
    }
    if (!!getTimeCheckIn?.withPersistResponse?.data?.timer) {
      const totalSecond =
        getTimeCheckIn?.response?.data?.totalSecond === 'null'
          ? 0
          : getTimeCheckIn?.response?.data?.totalSecond ?? 0;
      setTime(parseInt(totalSecond));
    }
    const facilityId = localStorage.getItem('facilityId');
    if (getTimeCheckIn?.isGetCheckIn && getTimeCheckIn?.response !== null) {
      if (
        getTimeCheckIn?.response?.data?.lastTimerStatus === 'CLOCK_IN' &&
        getTimeCheckIn?.response?.data?.facilityId === facilityId
      ) {
        setIsRunning(true);
        localStorage.setItem('isCheckIn', 'true');
        return;
      }
      if (
        getTimeCheckIn?.response?.data?.lastTimerStatus === 'CLOCK_OUT' &&
        getTimeCheckIn?.response?.data?.facilityId === facilityId
      ) {
        setIsRunning(false);
        localStorage.setItem('isCheckIn', 'false');
      }
      dispatch(getTimeCheckInActionReset());
    }
  }, [getTimeCheckIn]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (isRunning) {
      interval = setInterval(() => {
        setTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [isRunning]);

  const formattedTime = moment().startOf('day').seconds(time).format('HH:mm:ss');

  const startStopwatch = (clockInType: string, millageInfo: any) => {
    // const currentTime = new Date().toISOString();
    const currentTime = new Date();
    const dateTimeAccordingToFacility = convertDateToFacilityDate(currentTime);
    const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
    setGlobalLocationType(millageInfo?.locationType);
    localStorage.setItem('globalLocationType', millageInfo?.locationType);
    if (millageInfo?.locationType === 'MILEAGE') {
      dispatch(
        postTimeCheckOutAction({
          providerId,
          isLogout: false,
          idleTimeExpired: false,
          zoneId: getFacilityTimeZone,
          clockInBody: {
            locationType: clockInType === 'MILLAGE' ? millageInfo?.locationType : null,
            payByMilage: clockInType === 'MILLAGE' ? millageInfo?.payByMilage : null,
            clockInActivityType: millageInfo?.activityType ? millageInfo?.activityType : null,
          },
        })
      );
      return;
    }

    dispatch(
      postTimeCheckOutAction({
        providerId,
        date: convertIntoIsoFormat(dateTimeAccordingToFacility),
        zoneId: getFacilityTimeZone,
        timer: 'CLOCK_IN',
        subsequent: millageInfo?.isSubsequentBuilding,
        clockId: getTimeCheckIn?.withPersistResponse?.data?.uuid,
        isLogout: false,
        idleTimeExpired: false,
        clockInBody: {
          locationType: clockInType === 'MILLAGE' ? millageInfo?.locationType : null,
          payByMilage: clockInType === 'MILLAGE' ? millageInfo?.payByMilage : null,
          clockInActivityType: millageInfo?.activityType ? millageInfo?.activityType : null,
        },
      })
    );
  };

  const stopStopwatch = () => {
    // const currentTime = new Date().toISOString();
    const currentTime = new Date();
    const dateTimeAccordingToFacility = convertDateToFacilityDate(currentTime);
    const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
    const getActivityType = localStorage.getItem('activityType') || '';
    dispatch(
      postTimeCheckOutAction({
        providerId,
        date: convertIntoIsoFormat(dateTimeAccordingToFacility),
        zoneId: getFacilityTimeZone,
        timer: 'CLOCK_OUT',
        idleTimeExpired: false,
        clockId: getTimeCheckIn?.withPersistResponse?.data?.uuid,
        isLogout: false,
        clockInBody: {
          locationType: globalLocationType,
          payByMilage: null,
          clockInActivityType: getActivityType ? getActivityType : null,
        },
      })
    );
  };

  useEffect(() => {
    if (!checkoutResponse?.isGetCheckIn && checkoutResponse?.response === null) return;
    if (!checkoutResponse.isGetCheckIn && checkoutResponse?.response !== null) {
      dispatch(postTimeCheckOutActionResetLogout());
      return;
    }
    // const currentDate = new Date();
    const currentTime = new Date();
    const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
    const dateTimeAccordingToFacility = convertDateToFacilityDate(currentTime);
    if (checkoutResponse.isGetCheckIn && checkoutResponse?.response !== null) {
      if (checkoutResponse?.clockAction === 'CLOCK_IN_SUCCESS') {
        dispatch(
          getTimeCheckInAction({
            providerId,
            date: convertIntoIsoFormat(dateTimeAccordingToFacility),
            zoneTime: getFacilityTimeZone,
          })
        );
        dispatch(postCheckInOutActionReset());
      }

      if (checkoutResponse?.clockAction === 'CLOCK_OUT_SUCCESS') {
        if (checkoutResponse?.isLogout) {
          setIsRunning(false);
          localStorage.setItem('isCheckIn', 'false');
          dispatch(postCheckInOutActionReset());
          logoutUser();
          return;
        }
        dispatch(
          getTimeCheckInAction({
            providerId,
            date: convertIntoIsoFormat(dateTimeAccordingToFacility),
            zoneTime: getFacilityTimeZone,
          })
        );
        dispatch(postCheckInOutActionReset());
      }
    }
  }, [checkoutResponse]);

  const stopwatchContextValue: StopwatchContextProps = {
    formattedTime,
    isRunning,
    startStopwatch,
    stopStopwatch,
    time,
    setIsRunning,
    globalLocationType,
  };

  return (
    <StopwatchContext.Provider value={stopwatchContextValue}>{children}</StopwatchContext.Provider>
  );
};

export const useStopwatch = (): StopwatchContextProps => {
  const context = useContext(StopwatchContext);
  if (!context) {
    throw new Error('useStopwatch must be used within a StopwatchProvider');
  }
  return context;
};
